/**
 * CallbackForm.tsx — the free callback request (the human lane).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: launch lanes are Form + Callback ONLY. This form is the callback lane
 * everywhere it appears — the standalone callback page, the route-choice
 * hand-off, and INLINE on every hard-stop screen (a stop never ends in an
 * automated dead end; the free callback offer is always present). Posts
 * `POST /v1/clinic/callbacks` and shows the confirmation in place. A failed POST
 * renders a retryable error (TA-06). Copy is category-level only (R28). The
 * submit is the single terracotta conversion CTA on this surface.
 *
 * UX Wave 9 §5: the "best time" is one of the five shared TIME BLOCKS
 * (lib/timeBlocks.ts) — the wire carries the block value ("08:00-10:00"),
 * which clinic-service validates; the old free-text-ish presets are gone.
 *
 * Email field is conditional on `leadId` (2026-08-21): with no leadId there
 * is no Lead row yet to resolve an email from, so the CRM sync
 * (`syncDealOnCallbackCreated` in clinic-service) has nothing to create the
 * pipeline deal against — the ticket saved but never reached the Pipeline
 * board. Collecting email directly on this lane fixes that without asking
 * the medical questions the lead-capture form would otherwise require.
 */
import { useState, type FormEvent } from 'react';
import { createCallback } from '../api/journey';
import { isValidEmail } from '../auth/registerErrors';
import type { Attribution } from '../api/types';
import { TIME_BLOCKS, TIME_BLOCK_LABELS, type TimeBlock } from '../lib/timeBlocks';
import { isUkMobile, ukMobileHint } from '../lib/ukMobile';
import { Button, Field, Select, TextInput } from '../ui/kit';

/** Name/phone/preferred-time form → POST /v1/clinic/callbacks → confirmation. */
export function CallbackForm({
  clinicSlug,
  attribution,
  leadId,
  onSent,
}: {
  clinicSlug: string;
  attribution?: Attribution;
  leadId?: string;
  /**
   * Told once the request has gone through, so a host that opened this form
   * over something else (the in-journey dialog) can change what it offers
   * next. The confirmation itself still renders here, exactly as on every
   * other lane. Optional: the standalone page and the stop screens pass none.
   */
  onSent?: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredTime, setPreferredTime] = useState<TimeBlock>(TIME_BLOCKS[0]);
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  // No leadId means no email is on file yet (the standalone lane, or a
  // leadless hard-stop successor) — the CRM sync can only create the
  // pipeline deal by resolving a Lead's email, so this lane must collect one
  // directly. With a leadId, the email is already known via that Lead.
  const needsEmail = !leadId;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (state === 'busy') return;
    if (!firstName.trim() || !lastName.trim() || phone.trim().length < 7) {
      setError(
        needsEmail
          ? 'Please add your first name, last name, email address and a phone number we can reach you on.'
          : 'Please add your first name, last name and a phone number we can reach you on.',
      );
      return;
    }
    // A UK MOBILE, and said at the field (2026-09-11). The number is how we text
    // the patient before we ring and follow up by SMS/WhatsApp, neither of which
    // reaches a landline. clinic-service enforces the same rule and is the
    // control; this check exists so the patient reads WHY here, instead of the
    // generic failure a server 400 would otherwise surface as.
    if (!isUkMobile(phone)) {
      setError(ukMobileHint(phone));
      return;
    }
    // `isValidEmail` (lib/email via registerErrors re-export), not a local `includes('@')`. The
    // server validates with Zod's `.email()`, which requires a dot in the
    // domain — so a looser client check passes `sarah@example` through, the
    // request 400s, and the patient gets a generic failure banner instead of
    // being told which field is wrong. The shared helper is the same shape as
    // the server's, so the two agree on what a valid address is.
    if (needsEmail && !isValidEmail(email)) {
      setError(
        email.trim()
          ? 'That email address does not look right — please check it.'
          : 'Please add an email address we can send your confirmation to.',
      );
      return;
    }
    setError(null);
    setState('busy');
    try {
      await createCallback({
        clinicSlug,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        preferredTime,
        ...(needsEmail ? { email: email.trim() } : {}),
        ...(leadId ? { leadId } : {}),
        ...(attribution ? { attribution } : {}),
      });
      setState('done');
      onSent?.();
    } catch {
      setState('idle');
      setError('Sorry — that did not go through. Please try again.');
    }
  }

  if (state === 'done') {
    return (
      <div role="status">
        <h2 style={{ fontFamily: 'var(--gps-font-display)', color: 'var(--gps-green-text)', margin: '0 0 6px' }}>
          Thanks — we will be in touch
        </h2>
        <p style={{ color: 'var(--gps-text)', margin: 0 }}>
          A member of our team will call you back in your chosen window —{' '}
          {TIME_BLOCK_LABELS[preferredTime].toLowerCase()}. The callback is free, and there is no
          obligation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Request a callback" noValidate>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
        <Field label="First name" htmlFor="cb-first-name">
          <TextInput className="gps-journey-control" id="cb-first-name" placeholder="e.g. Sarah" value={firstName} autoComplete="given-name" onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name" htmlFor="cb-last-name">
          <TextInput className="gps-journey-control" id="cb-last-name" placeholder="e.g. Khan" value={lastName} autoComplete="family-name" onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>
      {needsEmail ? (
        <Field label="Email address" htmlFor="cb-email">
          <TextInput className="gps-journey-control" id="cb-email" type="email" inputMode="email" placeholder="e.g. sarah@example.com" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
        </Field>
      ) : null}
      <Field label="Phone number" htmlFor="cb-phone">
        <TextInput className="gps-journey-control" id="cb-phone" type="tel" inputMode="tel" placeholder="e.g. 07123 456789" value={phone} autoComplete="tel" onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field label="Best time to call" htmlFor="cb-time">
        <Select
          className="gps-journey-control"
          id="cb-time"
          value={preferredTime}
          onChange={(e) => setPreferredTime(e.target.value as TimeBlock)}
        >
          {TIME_BLOCKS.map((block) => (
            <option key={block} value={block}>
              {TIME_BLOCK_LABELS[block]}
            </option>
          ))}
        </Select>
      </Field>
      {error ? (
        <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px', margin: '4px 0' }}>
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="cta" disabled={state === 'busy'} style={{ marginTop: 'var(--gps-s-2)' }}>
        {state === 'busy' ? 'Sending…' : 'Request my free callback'}
      </Button>
      <p style={{ fontSize: '12px', color: 'var(--gps-muted)', marginTop: 'var(--gps-s-2)' }}>
        Free, no pressure — our team calls to talk things through, nothing more.
      </p>
    </form>
  );
}
